import { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,

  updateDoc,
  deleteDoc
} from "firebase/firestore";
import {
  getStudiesCopyPath,
  getStudyPatientsPath,
  getStudyPotentialPatientsPath,
} from "../services/firestorePaths";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Save,
  Search,
  Plus,
  Users,
  ChevronDown,
  X,
  Trash2,
  UserCircle2,
  FileText,
  CalendarDays,
  Hash,
  Loader2,
  Globe,
  ShieldCheck,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import PendingEnrollmentsPanel from "./PendingEnrollmentsPanel";
import { isMergedStudyCopy } from "../services/studyCopyMerge";
import { fetchPendingEnrollmentRequests } from "../services/enrollmentRequestService";


// Frontend-only display helpers: show timestamp-like fields (e.g. createdAt)
// in a human-friendly way. These never mutate the stored/edited value, so
// saves continue to write whatever is already in editData.
const TIMESTAMP_KEY_RE = /(created|updated|modified|timestamp)/i;

const isTimestampField = (key) => TIMESTAMP_KEY_RE.test(key || "");

const formatTimestampValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let date;
  if (typeof value === "object" && typeof value.seconds === "number") {
    // Firestore Timestamp shape
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "number") {
    date = new Date(value);
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    // epoch millis stored as a numeric string
    const asNum = /^\d{10,}$/.test(trimmed) ? Number(trimmed) : NaN;
    date = Number.isNaN(asNum) ? new Date(trimmed) : new Date(asNum);
  } else {
    return null;
  }
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const StudyPatientsPage = () => {
  const [studies, setStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [studySearch, setStudySearch] = useState("");
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [masterSearch, setMasterSearch] = useState("");
const [globalResults, setGlobalResults] = useState([]);
const [searchingGlobal, setSearchingGlobal] = useState(false);
// Display-only: studies the selected patient is already enrolled in, derived by
// matching their name across every study's `patients` subcollection. Purely
// informational — never written back and never affects any existing workflow.
const [enrolledStudies, setEnrolledStudies] = useState([]);
const [enrolledStudiesLoading, setEnrolledStudiesLoading] = useState(false);
const [patientView, setPatientView] = useState("enrolled");
// enrolled | potential


  // ✅ Add Patient Modal States
const [showAddModal, setShowAddModal] = useState(false);
const [newPatient, setNewPatient] = useState({
  studyId: "",
  name: "",
  dob: "",
  patientNumber: "",
});
const [adding, setAdding] = useState(false);

  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  // "list" = the page as it has always been; "pending" = admin approval queue.
  // UI revamp: the home page's "enrollments waiting" link opens straight on Pending.
  const [pageTab, setPageTab] = useState(() =>
    UI_REVAMP_ENABLED && isAdmin && location.state?.tab === "pending" ? "pending" : "list"
  );
  const [pendingCount, setPendingCount] = useState(0);
  // Show the waiting count on the tab before it's opened.
  useEffect(() => {
    if (!isAdmin) return;
    fetchPendingEnrollmentRequests()
      .then((r) => setPendingCount(r.length))
      .catch(() => {});
  }, [isAdmin]);


  const dropdownRef = useRef(null);
  const globalSearchRef = useRef(null);

  useEffect(() => {
  if (selectedStudy) {
    handleStudySelect(selectedStudy);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [patientView]);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Close global search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        globalSearchRef.current &&
        !globalSearchRef.current.contains(event.target)
      ) {
        setGlobalResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Fetch all studies
  useEffect(() => {
    const fetchStudies = async () => {
      try {
        const querySnap = await getDocs(collection(db, getStudiesCopyPath()));
        // Old lists merged into their current study (MERGED_INTO) are hidden:
        // their rows were copied to the current study's list, which is the
        // one to use. Nothing was deleted (see studyCopyMerge.js).
        const data = querySnap.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((s) => !isMergedStudyCopy(s));

        const formatted = data.map((s) => ({
          id: s.id,
          label:
            s.NAME && s.STUDY_ID
              ? `${s.NAME}: ${s.STUDY_ID}`
              : s.NAME
              ? s.NAME
              : s.STUDY_ID
              ? s.STUDY_ID
              : s.id,
          name: s.NAME || "",
          studyId: s.STUDY_ID || "",
        }));

        setStudies(formatted);
        setFilteredStudies(formatted);
      } catch (err) {
        console.error("Error fetching studies:", err);
      }
    };

    fetchStudies();
  }, []);

  // ✅ Filter studies based on search inside dropdown
  useEffect(() => {
    const lower = studySearch.toLowerCase();
    const filtered = studies.filter(
      (s) =>
        s.label.toLowerCase().includes(lower) ||
        s.name.toLowerCase().includes(lower) ||
        s.studyId.toLowerCase().includes(lower)
    );
    setFilteredStudies(filtered);
  }, [studySearch, studies]);

  // ✅ Fetch patients for a selected study
  const handleStudySelect = async (studyId) => {
    setSelectedStudy(studyId);
    setSelectedPatient(null);
    setPatients([]);
    setFilteredPatients([]);
    setShowDropdown(false);
    setLoading(true);

    try {
      const snap = await getDocs(
  collection(
    db,
    patientView === "potential"
      ? getStudyPotentialPatientsPath(studyId)
      : getStudyPatientsPath(studyId)
  )
);

      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setPatients(data);
      setFilteredPatients(data);
    } catch (err) {
      console.error("Error fetching patients:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Simple fuzzy match function using Levenshtein distance
const fuzzyMatch = (a, b, threshold = 0.6) => {
  if (!a || !b) return false;
  a = a.toLowerCase();
  b = b.toLowerCase();

  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }

  const distance = dp[m][n];
  const maxLen = Math.max(m, n);
  const similarity = 1 - distance / maxLen;

  return similarity >= threshold;
};


  const handleGlobalSearch = async () => {
  if (!masterSearch.trim()) {
    setGlobalResults([]);
    return;
  }

  setSearchingGlobal(true);
  setGlobalResults([]);

  try {
    const studiesSnap = await getDocs(collection(db, getStudiesCopyPath()));

    // ⚡ Fetch all patient subcollections in parallel
    const allResults = (
      await Promise.all(
        // Skip merged old lists: their rows also live on the current study's
        // list, so searching both would show each patient twice.
        studiesSnap.docs.filter((d) => !isMergedStudyCopy(d.data())).map(async (studyDoc) => {
          const patientsSnap = await getDocs(
            collection(db, getStudyPatientsPath(studyDoc.id))
          );

          // Match patients by name
          return patientsSnap.docs
            .map((doc) => ({
              studyId: studyDoc.id,
              patientId: doc.id,
              ...doc.data(),
            }))
            .filter((p) => {
  const name = (p.NAME || "").toLowerCase();
  const query = masterSearch.toLowerCase();
  return (
    name.includes(query) || fuzzyMatch(name, query, 0.65) // 65% similarity threshold
  );
})

        })
      )
    ).flat();

    setGlobalResults(allResults);
  } catch (err) {
    console.error("Error during global search:", err);
  } finally {
    setSearchingGlobal(false);
  }
};



  // ✅ Filter patients based on search
  useEffect(() => {
    const lower = patientSearch.toLowerCase();
    const filtered = patients.filter((p) =>
  ((p.NAME || p.name || "")).toLowerCase().includes(lower)
);

    setFilteredPatients(filtered);
  }, [patientSearch, patients]);

  // ✅ Display-only: derive which studies the selected patient is already
  // enrolled in by matching their name across every study's `patients`
  // subcollection. Read-only; never writes and never alters existing flows.
  useEffect(() => {
    const normalizeName = (v) =>
      (v || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

    const target = normalizeName(
      selectedPatient?.NAME || selectedPatient?.name
    );

    if (!target || studies.length === 0) {
      setEnrolledStudies([]);
      return;
    }

    let cancelled = false;

    const deriveEnrolledStudies = async () => {
      setEnrolledStudiesLoading(true);
      try {
        // 1) Firestore store: match name across every study's `patients` subcollection.
        const firestoreMatches = (
          await Promise.all(
            studies.map(async (study) => {
              try {
                const snap = await getDocs(
                  collection(db, getStudyPatientsPath(study.id))
                );
                const isEnrolled = snap.docs.some((d) => {
                  const data = d.data();
                  return normalizeName(data.NAME || data.name) === target;
                });
                return isEnrolled ? study : null;
              } catch {
                return null;
              }
            })
          )
        ).filter(Boolean);

        // "Enrolled" is derived ONLY from real enrollment records (the study's
        // `patients` subcollection above). The legacy Google Sheet is deliberately
        // NOT used here: its `study` field is a NOTE, not an enrollment fact — a
        // sheet row can name a study while its message is DNQ/screen-fail, so the
        // patient was never enrolled. Notes are shown separately; they do not
        // determine enrolled-vs-not.
        const combined = [];
        const seenLabels = new Set();
        for (const s of firestoreMatches) {
          const key = (s.label || "").trim().toLowerCase();
          if (!key || seenLabels.has(key)) continue;
          seenLabels.add(key);
          combined.push(s);
        }

        if (!cancelled) setEnrolledStudies(combined);
      } catch (err) {
        console.error("Error deriving enrolled studies:", err);
        if (!cancelled) setEnrolledStudies([]);
      } finally {
        if (!cancelled) setEnrolledStudiesLoading(false);
      }
    };

    deriveEnrolledStudies();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, studies]);

  // ✅ Load a specific patient's details
  const handlePatientSelect = async (studyId, patientId) => {
    try {
const docRef = doc(
  db,
  patientView === "potential"
    ? getStudyPotentialPatientsPath(studyId)
    : getStudyPatientsPath(studyId),
  patientId
);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        delete data.TIMESTAMP;
        setSelectedPatient({ id: docSnap.id, ...data });
        setEditData(data);
      }
    } catch (err) {
      console.error("Error fetching patient details:", err);
    }
  };

  const handleGlobalPatientClick = async (studyId, patientId) => {
  await handleStudySelect(studyId);
  await handlePatientSelect(studyId, patientId);
  setGlobalResults([]);
  setMasterSearch("");
};


  // ✅ Update patient details
  const handleSave = async () => {
    if (!selectedPatient || !selectedStudy) return;
    setSaving(true);

    try {
      const docRef = doc(
        db,
        getStudyPatientsPath(selectedStudy),
        selectedPatient.id
      );
      await updateDoc(docRef, editData);
      alert("✅ Patient details updated successfully!");
    } catch (err) {
      console.error("Error updating patient:", err);
      alert("❌ Failed to update patient details.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Delete selected patient
const handleDelete = async () => {
  if (!selectedPatient || !selectedStudy) return;

  const confirmDelete = window.confirm(
    `Are you sure you want to delete ${
      selectedPatient.NAME || "this patient"
    }?`
  );

  if (!confirmDelete) return;

  try {
    await deleteDoc(
      doc(db, getStudyPatientsPath(selectedStudy), selectedPatient.id)
    );
    alert("🗑️ Patient deleted successfully.");
    setSelectedPatient(null);
    await handleStudySelect(selectedStudy); // refresh the list
  } catch (err) {
    console.error("Error deleting patient:", err);
    alert("❌ Failed to delete patient.");
  }
};

  // ✅ Add New Patient to selected study
const handleAddPatient = async () => {
  if (!newPatient.studyId || !newPatient.name || !newPatient.dob || !newPatient.patientNumber) {
    alert("Please fill all fields before saving.");
    return;
  }

  setAdding(true);
  try {
    const patientRef = collection(
      db,
      getStudyPatientsPath(newPatient.studyId)
    );

    await addDoc(patientRef, {
      NAME: newPatient.name,
      DOB: newPatient.dob,
      PATIENT_NUMBER: newPatient.patientNumber,
      CREATED_AT: new Date(),
    });

    alert("✅ Patient added successfully!");
    setNewPatient({ studyId: "", name: "", dob: "", patientNumber: "" });
  } catch (err) {
    console.error("Error adding patient:", err);
    alert("❌ Failed to add patient.");
  } finally {
    setAdding(false);
  }
};

  const selectedStudyLabel =
    studies.find((s) => s.id === selectedStudy)?.label;

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <div className="flex items-center gap-2 min-w-0">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
                  <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                    <Users className="w-4 h-4 text-white" strokeWidth={2.4} />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                    Patient Flow
                  </div>
                  <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate">
                    Study & Patient Management
                  </h1>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isAdmin && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] uppercase tracking-wider text-slate-300">
                  <ShieldCheck className="w-3 h-3 text-purple-300" />
                  Read only
                </span>
              )}
              {isAdmin && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Plus className="w-3.5 h-3.5 relative" />
                  <span className="relative">Add Patient</span>
                </button>
              )}
            </div>
          </div>

          {/* Admin only: the master list, or coordinator enrollments waiting
              for approval onto it. The coordinator view has no pending tab. */}
          {isAdmin && (
            <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 mb-3">
              {[
                { key: "list", label: "Enrolled list" },
                { key: "pending", label: `Pending approval${pendingCount ? ` (${pendingCount})` : ""}` },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setPageTab(t.key)}
                  className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition ${
                    pageTab === t.key
                      ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {isAdmin && pageTab === "pending" && (
            <PendingEnrollmentsPanel onCountChange={setPendingCount} />
          )}

          {/* Selectors / global search */}
          {pageTab === "list" && (
          <section className="relative z-30 isolate rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 mb-4 overflow-visible">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none rounded-2xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-x-5 md:gap-y-3">
              {/* Study Dropdown */}
              <div
                className={`relative min-w-0 ${
                  showDropdown ? "z-40" : "z-20"
                }`}
                ref={dropdownRef}
              >
                <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                  Select Study
                </span>
                <div
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="cursor-pointer flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.06] hover:border-purple-300/30 transition text-sm text-slate-100"
                >
                  <span className="truncate">
                    {selectedStudy
                      ? selectedStudyLabel
                      : "— Choose a Study —"}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${
                      showDropdown ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {showDropdown && (
                  <div className="study-dropdown-menu absolute top-full mt-1.5 left-0 right-0 z-50 rounded-xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(124,58,237,0.5)] overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                      <Search className="w-4 h-4 text-slate-400 pointer-events-none shrink-0" />
                      <input
                        type="text"
                        placeholder="Search studies…"
                        className="w-full min-w-0 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                        value={studySearch}
                        onChange={(e) => setStudySearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <ul className="max-h-[min(60vh,18rem)] overflow-y-auto overscroll-contain py-1">
                      {filteredStudies.map((study) => (
                        <li
                          key={study.id}
                          onClick={() => handleStudySelect(study.id)}
                          className={`px-3 py-2 mx-1 rounded-lg cursor-pointer text-xs transition flex items-center gap-2 ${
                            selectedStudy === study.id
                              ? "bg-purple-500/15 text-white border border-purple-300/30"
                              : "text-slate-200 hover:bg-white/[0.05] border border-transparent"
                          }`}
                        >
                          <FileText className="w-3 h-3 text-purple-300 shrink-0" />
                          <span className="truncate">{study.label}</span>
                        </li>
                      ))}
                      {filteredStudies.length === 0 && (
                        <li className="px-4 py-2 text-slate-500 text-xs">
                          No results found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Global Search */}
              <div
                ref={globalSearchRef}
                className={`relative min-w-0 ${
                  globalResults.length > 0 ? "z-40" : "z-10"
                }`}
              >
                <span className="text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1 flex items-center gap-1.5">
                  <Globe className="w-3 h-3 text-purple-300" />
                  Search across all studies
                </span>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search patient by name…"
                    className="w-full pl-9 pr-16 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                    value={masterSearch}
                    onChange={(e) => setMasterSearch(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleGlobalSearch()
                    }
                  />
                  <button
                    onClick={handleGlobalSearch}
                    disabled={searchingGlobal}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 ring-1 ring-white/20 text-[11px] font-semibold text-white hover:shadow-[0_6px_16px_-4px_rgba(139,92,246,0.6)] active:scale-95 transition disabled:opacity-60"
                  >
                    {searchingGlobal ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      "Go"
                    )}
                  </button>
                </div>

                {globalResults.length > 0 && (
                  <div className="study-dropdown-menu absolute top-full mt-1.5 left-0 right-0 z-50 rounded-xl border border-white/10 bg-[#0c0826] backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(124,58,237,0.5)] max-h-[min(60vh,18rem)] overflow-y-auto overscroll-contain py-1">
                    {globalResults.map((r) => (
                      <div
                        key={r.patientId}
                        onClick={() =>
                          handleGlobalPatientClick(r.studyId, r.patientId)
                        }
                        className="mx-1 px-3 py-2 rounded-lg cursor-pointer text-xs hover:bg-white/[0.05] transition flex items-center gap-2"
                      >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                          {(r.NAME || "?").charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white font-medium truncate">
                          {r.NAME}
                        </span>
                        <span className="ml-auto text-[10px] font-mono text-purple-300/80 shrink-0">
                          {studies.find((s) => s.id === r.studyId)?.label || r.studyId}
                        </span>
                      </div>
                    ))}
                    {globalResults.length === 0 && !searchingGlobal && (
                      <p className="px-4 py-2 text-slate-500 text-xs">
                        No matches found
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
          )}

          {/* Loading */}
          {pageTab === "list" && loading && (
            <div className="flex items-center justify-center py-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin text-purple-300" />
                Loading patients…
              </div>
            </div>
          )}

          {/* Patient list + detail */}
          {pageTab === "list" && !loading && selectedStudy && (
            <section className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {/* Patient list */}
              <div className="md:col-span-2 relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none rounded-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-3.5 h-3.5 text-purple-300" />
                    <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                      {patientView === "potential"
                        ? "Potential Patients"
                        : "Enrolled Patients"}
                    </h2>
                    <span className="ml-auto text-[10px] text-slate-400">
                      {filteredPatients.length}
                    </span>
                  </div>

                  {/* Toggle pills */}
                  <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 mb-3">
                    <button
                      onClick={() => setPatientView("enrolled")}
                      className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition ${
                        patientView === "enrolled"
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                          : "text-slate-300 hover:text-white"
                      }`}
                    >
                      Enrolled
                    </button>
                    <button
                      onClick={() => setPatientView("potential")}
                      className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition ${
                        patientView === "potential"
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                          : "text-slate-300 hover:text-white"
                      }`}
                    >
                      Potential
                    </button>
                  </div>

                  {/* Patient search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search patients…"
                      className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                    />
                  </div>

                  {filteredPatients.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center">
                      <UserCircle2 className="w-6 h-6 text-slate-500 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">No patients found.</p>
                    </div>
                  ) : (
                    <ul className="space-y-1 max-h-[520px] overflow-y-auto pr-1">
                      {filteredPatients.map((p) => {
                        const isActive = selectedPatient?.id === p.id;
                        const name = p.NAME || p.name || "Unnamed Patient";
                        return (
                          <li
                            key={p.id}
                            onClick={() =>
                              handlePatientSelect(selectedStudy, p.id)
                            }
                            className={`group flex items-center gap-2 cursor-pointer px-2.5 py-2 rounded-lg border transition ${
                              isActive
                                ? "bg-purple-500/15 border-purple-300/30 text-white shadow-[inset_0_0_20px_rgba(139,92,246,0.2)]"
                                : "border-transparent text-slate-200 hover:bg-white/[0.05] hover:border-white/10"
                            }`}
                          >
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                                isActive
                                  ? "bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 text-white ring-1 ring-white/20"
                                  : "bg-white/[0.04] border border-white/10 text-slate-300"
                              }`}
                            >
                              {name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm truncate">{name}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              {/* Patient detail */}
              {selectedPatient ? (
                <div className="md:col-span-3 relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none rounded-2xl" />
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70" />
                  <div className="relative">
                    {/* Header */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
                          {(selectedPatient.NAME || selectedPatient.name || "?")
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                            Patient Details
                          </div>
                          <h2 className="text-sm font-semibold text-white truncate leading-tight">
                            {selectedPatient.NAME || selectedPatient.name || "Unnamed"}
                          </h2>
                        </div>
                      </div>
                      {!isAdmin && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-400/15 text-slate-300 border border-slate-400/30">
                          <ShieldCheck className="w-3 h-3" />
                          Read only
                        </span>
                      )}
                    </div>

                    {/* Enrolled Studies (display-only) — studies this patient
                        already appears in, matched by name. Hidden when there
                        are no matches and nothing is loading. */}
                    {(enrolledStudiesLoading || enrolledStudies.length > 0) && (
                      <div className="mb-3 rounded-xl border border-emerald-300/25 bg-emerald-500/[0.07] p-2.5">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <FileText className="w-3 h-3 text-emerald-300 shrink-0" />
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-200">
                            Enrolled Studies
                          </span>
                          <span className="ml-auto text-[10px] text-emerald-300/80">
                            {enrolledStudiesLoading
                              ? "Checking…"
                              : `${enrolledStudies.length} found`}
                          </span>
                        </div>
                        <div className="mb-2 flex items-center gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-2 py-1 text-[10px] font-medium text-amber-200">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          Beta — double-check before relying on it.
                        </div>
                        {enrolledStudiesLoading ? (
                          <p className="text-[11px] text-slate-400">
                            Checking enrollment across studies…
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {enrolledStudies.map((s) => (
                              <span
                                key={s.id}
                                className="inline-flex items-center gap-1 rounded-lg border border-emerald-300/30 bg-emerald-500/15 px-2 py-1 text-[11px] font-medium text-emerald-100"
                              >
                                <span className="truncate max-w-[220px]">
                                  {s.label}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[440px] overflow-y-auto pr-1">
                      {Object.entries(editData).map(([key, value]) => {
                        const friendlyTimestamp = isTimestampField(key)
                          ? formatTimestampValue(value)
                          : null;
                        return (
                          <label key={key} className="block">
                            <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                              {key}
                            </span>
                            {friendlyTimestamp ? (
                              // Display-only friendly timestamp; editData is left
                              // untouched so saving still writes the raw value.
                              <div className="w-full px-3 py-2 rounded-xl text-sm border bg-white/[0.02] border-white/5 text-slate-300">
                                {friendlyTimestamp}
                              </div>
                            ) : (
                              <input
                                type="text"
                                value={value || ""}
                                readOnly={!isAdmin}
                                onChange={
                                  isAdmin
                                    ? (e) =>
                                        setEditData({
                                          ...editData,
                                          [key]: e.target.value,
                                        })
                                    : undefined
                                }
                                className={`w-full px-3 py-2 rounded-xl text-sm transition border ${
                                  isAdmin
                                    ? "bg-white/[0.04] border-white/10 text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20"
                                    : "bg-white/[0.02] border-white/5 text-slate-400 cursor-not-allowed"
                                }`}
                              />
                            )}
                          </label>
                        );
                      })}
                    </div>

                    <button
                      onClick={handleSave}
                      disabled={saving || !isAdmin}
                      className={`group relative mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold ring-1 ring-white/20 transition-all overflow-hidden ${
                        !isAdmin || saving
                          ? "bg-white/[0.05] text-slate-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98]"
                      }`}
                    >
                      {isAdmin && !saving && (
                        <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                      )}
                      <Save className="w-4 h-4 relative" />
                      <span className="relative">
                        {!isAdmin
                          ? "View Only"
                          : saving
                          ? "Saving…"
                          : "Save Changes"}
                      </span>
                    </button>

                    {isAdmin && (
                      <button
                        onClick={handleDelete}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-semibold text-rose-200 bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 hover:border-rose-400/50 hover:text-white transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Patient
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="md:col-span-3 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
                  <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                    <UserCircle2 className="w-5 h-5 text-purple-300" />
                  </div>
                  <h3 className="text-sm font-semibold text-white">
                    No patient selected
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Pick a patient from the list to view details.
                  </p>
                </div>
              )}
            </section>
          )}

          {/* No study selected */}
          {pageTab === "list" && !loading && !selectedStudy && (
            <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-purple-300" />
              </div>
              <h3 className="text-sm font-semibold text-white">Select a study</h3>
              <p className="text-xs text-slate-400 mt-1">
                Choose a study above to view its patient list.
              </p>
            </div>
          )}

          {/* Add Patient Modal */}
          <AnimatePresence>
            {showAddModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setShowAddModal(false);
                }}
              >
                <motion.div
                  initial={{ y: 18, scale: 0.98, opacity: 0 }}
                  animate={{ y: 0, scale: 1, opacity: 1 }}
                  exit={{ y: 18, scale: 0.98, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="relative w-full max-w-md max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col"
                >
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 opacity-80 pointer-events-none" />
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-emerald-500/15 blur-[120px]" />
                    <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-blue-600/15 blur-[120px]" />
                  </div>

                  {/* Header */}
                  <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
                    <div className="relative shrink-0">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl blur-md opacity-70" />
                      <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-cyan-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-white" strokeWidth={2.4} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-semibold leading-tight">
                        Patient Flow
                      </div>
                      <h2 className="text-base font-semibold tracking-tight text-white leading-tight">
                        Add New Patient
                      </h2>
                    </div>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                      aria-label="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="relative overflow-y-auto flex-1 p-5 space-y-3">
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Select Study
                      </span>
                      <div className="relative">
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <select
                          value={newPatient.studyId}
                          onChange={(e) =>
                            setNewPatient({
                              ...newPatient,
                              studyId: e.target.value,
                            })
                          }
                          className="w-full appearance-none pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition"
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            -- Choose a Study --
                          </option>
                          {studies.map((s) => (
                            <option
                              key={s.id}
                              value={s.id}
                              className="bg-[#0b0820] text-slate-100"
                            >
                              {s.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </label>

                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Patient Name
                      </span>
                      <div className="relative">
                        <UserCircle2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="text"
                          value={newPatient.name}
                          onChange={(e) =>
                            setNewPatient({ ...newPatient, name: e.target.value })
                          }
                          placeholder="Enter name"
                          className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                        />
                      </div>
                    </label>

                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Date of Birth
                      </span>
                      <div className="relative">
                        <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="date"
                          value={newPatient.dob}
                          onChange={(e) =>
                            setNewPatient({ ...newPatient, dob: e.target.value })
                          }
                          className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]"
                        />
                      </div>
                    </label>

                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                        Patient #
                      </span>
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="text"
                          value={newPatient.patientNumber}
                          onChange={(e) =>
                            setNewPatient({
                              ...newPatient,
                              patientNumber: e.target.value,
                            })
                          }
                          placeholder="Enter Patient Number"
                          className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                        />
                      </div>
                    </label>
                  </div>

                  {/* Footer */}
                  <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
                    <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
                      <Sparkles className="w-3 h-3 text-emerald-300" />
                      New patient
                    </div>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddPatient}
                      disabled={adding}
                      className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                      {adding ? (
                        <Loader2 className="w-4 h-4 relative animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 relative" />
                      )}
                      <span className="relative">
                        {adding ? "Adding…" : "Add Patient"}
                      </span>
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default StudyPatientsPage;
